import { Link } from "@inertiajs/react";
import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import type HomeClientLink from "@/types/serializers/HomeClientLink";
import type HomePersonTask from "@/types/serializers/HomePersonTask";

type RelationshipType = HomeClientLink["relationship"];

const RELATIONSHIP_PRIORITY: Record<RelationshipType, number> = {
	ubo: 0,
	statutory_member: 0,
	ubo_and_statutory: 0,
	direct_entrepreneur: 1,
	direct_individual: 2,
};

const DIRECT_CLIENT_LABELS: Record<
	"direct_individual" | "direct_entrepreneur",
	string
> = {
	direct_individual: "Fyzická osoba",
	direct_entrepreneur: "OSVČ",
};

function isDirectRelationship(
	relationship: RelationshipType,
): relationship is "direct_individual" | "direct_entrepreneur" {
	return (
		relationship === "direct_individual" ||
		relationship === "direct_entrepreneur"
	);
}

function getClientLinkDisplayText(link: HomeClientLink): string {
	if (isDirectRelationship(link.relationship)) {
		return DIRECT_CLIENT_LABELS[link.relationship];
	}
	return link.client_name;
}

function sortClientLinks(links: HomeClientLink[]): HomeClientLink[] {
	return [...links].sort((a, b) => {
		const priorityDiff =
			RELATIONSHIP_PRIORITY[a.relationship] -
			RELATIONSHIP_PRIORITY[b.relationship];
		if (priorityDiff) return priorityDiff;
		return a.client_name.localeCompare(b.client_name);
	});
}

interface PersonTaskItemProps {
	task: HomePersonTask;
	description: string;
	isExpired?: boolean;
}

export function PersonTaskItem({
	task,
	description,
	isExpired = false,
}: PersonTaskItemProps) {
	const sortedLinks = sortClientLinks(task.client_links);
	const primaryLink = sortedLinks[0];
	const additionalCount = sortedLinks.length - 1;

	const primaryBadgeText = primaryLink
		? getClientLinkDisplayText(primaryLink)
		: "";

	const badgeClassName = isExpired ? "bg-card text-xs" : "text-xs";

	const badgeContent =
		additionalCount > 0 ? (
			<Tooltip>
				<TooltipTrigger
					render={
						<Badge
							variant="outline"
							className={`cursor-default ${badgeClassName}`}
						>
							{primaryBadgeText} +{additionalCount}
						</Badge>
					}
				/>
				<TooltipContent>
					<ul className="space-y-1">
						{sortedLinks.map((link) => (
							<li key={link.client_id}>{getClientLinkDisplayText(link)}</li>
						))}
					</ul>
				</TooltipContent>
			</Tooltip>
		) : (
			<Badge variant="outline" className={badgeClassName}>
				{primaryBadgeText}
			</Badge>
		);

	const baseClassName =
		"flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted";
	const expiredClassName = isExpired ? "bg-destructive/10" : "";

	return (
		<Link
			href={`/physical_persons/${task.person_id}`}
			className={`${baseClassName} ${expiredClassName}`}
		>
			<div className="flex items-center gap-2">
				<span className="whitespace-nowrap font-medium">
					{task.person_name}
				</span>
				{badgeContent}
			</div>
			<span className="truncate pl-3 text-muted-foreground text-sm">
				{description}
			</span>
		</Link>
	);
}
